import { useManagementGetCompanies } from '@apps-common/apis/management/company'
import { ButtonRoundSm, ControlledInputDebounced, SelectInputSM } from '@apps-common/components/elements'
import { ModalBody, ModalHeader, ModalHeaderContainer } from '@apps-common/components/modals/modal'
import { commonIcons } from '@apps-common/config/icons'
import {
  INPUT_CLASSES,
  INPUT_CONTAINER_CLASSES,
  INPUT_DATEPICKER_CLASSES_SM,
  INPUT_DATEPICKER_CONTAINER_SM
} from '@apps-common/config/input-styles'
import { ModalDismiss } from '@apps-common/config/modal'
import { ORDER_STATUS_VALUES } from '@apps-common/config/orders'
import { AUTH_CLAIMS, Authorization } from '@apps-common/lib/authorization'
import {
  getPortalOrderFilterDefaultEndDateStr,
  getPortalOrderFilterDefaultStartDateStr,
  usePortalOrderFilterStore
} from '@apps-common/stores'

import { ControlledDatepickerInputSM } from '../elements/form/ControlledDatepickerInputSM'
import { FilterModalContainer } from './filter/FilterModalContainer'

interface IProps {
  value: string
  onChange: (e: any) => void
}

const PortalOrderFilterCompanies: React.FC<IProps> = ({ value, onChange }) => {
  const {
    data: companies,
    isLoading: companiesIsLoading,
    isError: companiesIsError
  } = useManagementGetCompanies({
    config: {
      staleTime: 60000
    },
    name: ''
  })

  const loading = companiesIsLoading || companiesIsError

  return (
    <SelectInputSM
      placeholder='All companies'
      label='Company'
      showLabel
      id='order-filter-company'
      className={INPUT_CLASSES}
      containerClassName={INPUT_CONTAINER_CLASSES}
      loading={loading}
      options={companies?.map((c) => ({ label: c.companyName, value: c.companyId })) || []}
      hideError
      registration={{ value, onChange }}
    />
  )
}

export const PortalOrderFilterModal = ({
  onDismiss
}: {
  onDismiss: (data?: string | null | undefined | number, role?: string) => void
}) => {
  const state = usePortalOrderFilterStore()

  return (
    <FilterModalContainer onDismiss={onDismiss}>
      <ModalHeaderContainer>
        <div className='flex items-start justify-between'>
          <ModalHeader subtitle='Orders' title='Filter' subtitleClasses='capitalize' />
          <div className='flex items-center gap-[10px]'>
            <ButtonRoundSm
              type='button'
              className='action-brand'
              callback={() => {
                state.clear()
                onDismiss(null, ModalDismiss.Confirm)
              }}
            >
              <commonIcons.reload />
            </ButtonRoundSm>
            <ButtonRoundSm
              type='button'
              className='action-brand-solid'
              callback={() => {
                onDismiss(null, ModalDismiss.Cancel)
              }}
            >
              <commonIcons.close />
            </ButtonRoundSm>
          </div>
        </div>
      </ModalHeaderContainer>
      <ModalBody classes='relative !pt-[85px] !pb-[30px] sm:!pb-[20px]'>
        <div className='grid gap-[10px]'>
          {/* ORDER # */}
          <ControlledInputDebounced
            type='text'
            placeholder='Search by Order #'
            label='Order #'
            showLabel
            id='order-filter-order-no'
            className={INPUT_CLASSES}
            containerClassName={INPUT_CONTAINER_CLASSES}
            value={state.orderNo}
            onChange={(e) => state.setOrderNo(e?.target?.value || '')}
          />
          {/* Company */}
          <Authorization roles={AUTH_CLAIMS.managementAdmin.roles} scopes={AUTH_CLAIMS.managementAdmin.scopes}>
            <PortalOrderFilterCompanies value={state.companyId} onChange={(e: any) => state.setCompanyId(e?.target?.value || '')} />
          </Authorization>
          {/* STATUS */}
          <SelectInputSM
            placeholder='All Statuses'
            label='Status'
            showLabel
            id='order-filter-status'
            className={INPUT_CLASSES}
            containerClassName={INPUT_CONTAINER_CLASSES}
            options={[{ label: 'All Statuses', value: '' }, ...ORDER_STATUS_VALUES]}
            hideError
            registration={{ value: state.status, onChange: (e: any) => state.setStatus(e?.target?.value || '') }}
          />
          <ControlledDatepickerInputSM
            label='Start date'
            id='order-filter-start-date'
            name='startDate'
            placeholder='Start date'
            showLabel
            className={INPUT_DATEPICKER_CLASSES_SM}
            containerClassName={INPUT_DATEPICKER_CONTAINER_SM}
            value={state.startDate || getPortalOrderFilterDefaultStartDateStr()}
            onChange={(value) => state.setStartDate(value)}
            minDate={new Date(getPortalOrderFilterDefaultStartDateStr())}
            maxDate={new Date(getPortalOrderFilterDefaultEndDateStr())}
          />
          <ControlledDatepickerInputSM
            label='End date'
            id='order-filter-end-date'
            name='endDate'
            placeholder='End date'
            showLabel
            className={INPUT_DATEPICKER_CLASSES_SM}
            containerClassName={INPUT_DATEPICKER_CONTAINER_SM}
            value={state.endDate || getPortalOrderFilterDefaultEndDateStr()}
            onChange={(value) => state.setEndDate(value)}
            minDate={new Date(getPortalOrderFilterDefaultStartDateStr())}
            maxDate={new Date(getPortalOrderFilterDefaultEndDateStr())}
          />
        </div>
      </ModalBody>
    </FilterModalContainer>
  )
}
